import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { KeyCodes } from '~/keycodes';
import AutoPosition from '../../autoposition';
import { apiGet, handleApiError } from '../../common';
import UsersAndGroupsList from './users-and-groups-list';

/** @deprecated prefer `import { AutocompleteField } from '@workramp/ui';` */
class AutocompleteInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],

      activeSuggestion: undefined,
      showSuggestions: false,

      suggestionPosition: {
        top: 'auto',
        right: 'auto',
        bottom: 'auto',
        left: 'auto',
      },
    };
  }

  UNSAFE_componentWillMount() {
    this.debouncedSearch = _.debounce(this.search, 250);
  }

  componentDidUpdate(prevProps, prevState) {
    const { suggestions, alwaysDropdown } = this.props;
    const { isMentioning } = this.state;
    if (prevProps.suggestions !== suggestions) {
      this.setState({ suggestions });
    }
    if (!prevState.isMentioning && isMentioning) {
      this.setState({
        suggestionPosition: AutoPosition.calculateMenuPosition(this.input, alwaysDropdown),
      });
    }
  }

  render() {
    return (
      <div className="autocomplete-input-wrapper">
        {this.getSuggestions()}
        <input
          ref={(input) => (this.input = input)}
          value={this.props.value || ''}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          className={
            this.props.overwriteInputCssClass
              ? this.props.overwriteInputCssClass
              : 'input-editor input-editor-input autocomplete-input'
          }
          autoFocus={this.props.autoFocus}
          placeholder={this.props.placeholder}
        />
        {this.props.showClearIcon && this.props.value && (
          <ClearIcon
            src="/images/cross_icon_blue.svg"
            className="cursor-pointer"
            onClick={this.handleClearClick}
            width="10px"
            height="10px"
          />
        )}
      </div>
    );
  }

  handleChange = (e) => {
    this.props.onChange(e);
    const { value } = e.target;

    if (value) {
      this.setState({ showSuggestions: true });
      this.debouncedSearch(value);
    } else {
      this.closeSuggestions();
      this.setState({ suggestions: [] });
    }
  };

  search = (mentionQuery, cb) => {
    // if a list of suggestions is passed instead of a search url, do not make api call
    if (!this.props.searchUrl && this.props.suggestions) return;
    let searchUrl = this.props.searchURL + encodeURIComponent(mentionQuery);
    if (this.props.limit) searchUrl += `&limit=${this.props.limit}`;

    if (mentionQuery) {
      apiGet(
        searchUrl,
        {},
        (result) => {
          const limitedResult = result.slice(0, this.props.limit || 10);
          this.setState({ isMentioning: true, suggestions: limitedResult });
          if (typeof cb === 'function') cb(result);
        },
        handleApiError(),
      );
    }
  };

  handleEntryClick = (suggestion) => {
    this.props.onEntryClick(suggestion);
    this.closeSuggestionsAndClearInput();
  };

  closeSuggestionsAndClearInput = () => {
    this.clearInput();
    this.closeSuggestions();
  };

  handleClearClick = () => {
    if (typeof this.props.onClearClick === 'function') {
      this.props.onClearClick();
    }
    this.clearInput();
    this.closeSuggestions();
  };

  getSuggestions = () => {
    if (!this.showSuggestions()) return;
    const { useUsersAndGroupsDropdown, customResultsDropdown } = this.props;
    const results = this.state.suggestions;

    if (customResultsDropdown) {
      return customResultsDropdown(results, this.closeSuggestionsAndClearInput, this.state.activeSuggestion);
    }
    const { top, bottom, left, right } = this.state.suggestionPosition;
    return (
      <div
        ref={(suggestions) => (this.suggestions = suggestions)}
        className="mention-enabled-text-field-suggestions"
        style={{ top, right, bottom, left }}
      >
        {useUsersAndGroupsDropdown ? (
          <UsersAndGroupsList
            entries={results}
            activeIndex={this.state.activeSuggestion}
            onEntryClick={this.handleEntryClick}
            whiteBackground
          />
        ) : (
          <AutoCompleteSuggestionsList>
            {_.map(results, (suggestion, index) => {
              const backgroundColor = index === this.state.activeSuggestion ? '#F9F9F9' : '#FFF';

              return (
                <AutoCompleteSuggestionItem
                  key={index}
                  backgroundColor={backgroundColor}
                  onClick={() => this.handleEntryClick(suggestion)}
                >
                  {typeof suggestion === 'string' ? (
                    suggestion
                  ) : suggestion.name && !suggestion.isGroup ? (
                    <span>
                      <span>{suggestion.name}</span>
                      <span>{suggestion.label}</span>
                    </span>
                  ) : (
                    suggestion.label
                  )}
                </AutoCompleteSuggestionItem>
              );
            })}
          </AutoCompleteSuggestionsList>
        )}
      </div>
    );
  };

  showSuggestions = () => this.state.suggestions.length > 0 && this.state.showSuggestions;

  closeSuggestions = () => {
    this.setState({
      activeSuggestion: undefined,
      showSuggestions: false,
      suggestions: [],
    });
  };

  nextSuggestion = () => {
    let { suggestions, activeSuggestion } = this.state;
    const maxIndex = suggestions.length - 1;

    if (_.isUndefined(activeSuggestion)) {
      activeSuggestion = 0;
    } else {
      activeSuggestion = activeSuggestion === maxIndex ? 0 : activeSuggestion + 1;
    }

    this.setState({ activeSuggestion });
  };

  previousSuggestion = () => {
    let { suggestions, activeSuggestion } = this.state;
    const maxIndex = suggestions.length - 1;

    if (_.isUndefined(activeSuggestion)) {
      activeSuggestion = maxIndex;
    } else {
      activeSuggestion = activeSuggestion === 0 ? maxIndex : activeSuggestion - 1;
    }

    this.setState({ activeSuggestion });
  };

  suggestionMatchesInput = (suggestion, search) => [suggestion.name, suggestion.label].includes(search);

  exactlyMatchedOrFirstSuggestion = (possibleSuggestions, search) => {
    if (possibleSuggestions.length === 0) return null;
    const matchedSuggestion = _.find(possibleSuggestions, (suggestion) =>
      this.suggestionMatchesInput(suggestion, search),
    );
    // if we dont have a match pick the first one in dropdown
    const suggestionToReturn = matchedSuggestion === undefined ? possibleSuggestions[0] : matchedSuggestion;
    return suggestionToReturn;
  };

  submitSuggestionAndClearInput = (suggestionToReturn) => {
    this.props.onEntryClick(suggestionToReturn);
    this.clearInput();
  };

  handleKeyDown = (e) => {
    const { suggestions, activeSuggestion } = this.state;
    const search = this.props.value;
    let suggestionToReturn;

    switch (e.keyCode) {
      case KeyCodes.DOWN_ARROW:
        if (this.showSuggestions()) {
          this.nextSuggestion();
        }
        break;
      case KeyCodes.UP_ARROW:
        if (this.showSuggestions()) {
          this.previousSuggestion();
        }
        break;
      case KeyCodes.ENTER:
        e.preventDefault();

        if (this.showSuggestions()) {
          if (!_.isUndefined(activeSuggestion)) {
            // a suggestion is highlighted - let's select that one
            this.submitSuggestionAndClearInput(suggestions[activeSuggestion]);
          } else {
            suggestionToReturn = this.exactlyMatchedOrFirstSuggestion(suggestions, search);
            this.submitSuggestionAndClearInput(suggestionToReturn);
          }
        } else if (this.props.forceSelectFromSuggestions) {
          // forces a search. only happens if user presses enter before suggestions have populated from debouncedSearch
          this.search(this.props.value, (receivedSuggestions) => {
            suggestionToReturn = this.exactlyMatchedOrFirstSuggestion(receivedSuggestions, this.props.value);
            if (!suggestionToReturn) return; // just let user hit enter
          });
        } else if ($(this.input).val() && $(this.input).val().trim()) {
          this.submitSuggestionAndClearInput($(this.input).val().trim());
        } else {
          this.props.submitCallback();
        }
        this.closeSuggestions();
        break;
      case KeyCodes.ESC:
        e.preventDefault();
        this.closeSuggestions();
        break;
      default:
        break;
    }
  };

  clearInput = () => {
    $(this.input).val('');
  };
}

const AutoCompleteSuggestionsList = styled.ul`
  border: 1px solid #f3f3f3;
  border-radius: 4px;
  list-style: none;
  padding: 0px;
  margin: 0px;
`;

const AutoCompleteSuggestionItem = styled.li`
  position: relative;
  margin: 0;
  padding: 0.35rem 1.2rem;
  font-size: 0.85rem;
  cursor: pointer;
  min-height: 0;
  background-color: ${(props) => props.backgroundColor};

  &:hover {
    border: 0;
    background: none;
    background-color: #f9f9f9;
  }
`;

const ClearIcon = styled.img`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

export default AutocompleteInput;
